import Alpine from '@alpinejs/csp';
import {WidgetConfig} from "types/config";
import ConfigLoader from 'config/configLoader';
import htmlLayout from '../../../html/widget_layout.html?raw';
import htmlCloseContent from '../../../html/widget_close_content.html?raw';
import htmlUsernameContent from '../../../html/widget_username_content.html?raw';
import htmlMessagesContent from '../../../html/widget_messages_content.html?raw';
import ComponentConfig from '../../config/componentConfig';
import {AgentMessageObject, AiHistoryMessage, BotMessageObject, Chat, OutputMessageObject} from "../../types/chat";
import ChatMaker from "../chatMaker";
import ChatHelper from "./chatHelper";
import BotChat from "../botChat";
import AgentChat from "../agentChat";
import {isBotMessageObject} from "../utils/typeUtils";
import {MessageFactory} from "../factory/messageFactory";
import {ChatType, ClosePageContentType, SystemMessage} from "../../types/enums";
import {CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT, IChatMessageInputHasTextEventDetails} from "../../types/events";
import UserData from "../user/UserData";
import {PrintPage} from "../utils/PrintPage";
import AlpineComponent from "../response/alpineComponent";
import {DomainValidator} from "../utils/domainValidator";
import {ClientErrorLogger} from "../utils/clientErrorLogger";
import {HtmlToMarkdownConverter} from "../utils/HtmlToMarkdownConverter";
import usernameFormFactory from "classes/chat/components/usernameForm";
import messageContentFactory from "classes/chat/components/messageContent";
import feedbackComponentFactory from "classes/chat/components/feedbackComponent";
import speechMessageFactory from "classes/chat/components/speechMessage";

const config = await ConfigLoader.getConfig();
export class ChatWidget extends HTMLElement {

    private innerContentReplaceDiv: string = '<div id="chat-widget-inner-content"></div>';
    private fullscreenClassName: string = 'chat-widget-fullscreen';

    private readonly widgetDOM: ShadowRoot;
    private readonly widgetConfig: WidgetConfig;
    private readonly componentConfig: ComponentConfig;
    public activeChat: null|Chat = null;
    private reportingChat: null|BotChat = null;
    private chatHelper: null|ChatHelper = null;
    private isClientTyping = false;
    private holdSessionIntervalId: null|number = null;
    private endClientTyping = false;
    private userData: null|UserData = null;
    private alpineComponent: null|AlpineComponent = null;
    private callId: null|string = null;
    private chatWindowChannel: null|BroadcastChannel = null;
    private aiHistoryMessages: AiHistoryMessage[] = [];
    private aiHistoryMessageIndex: number = 0;
    private htmlToMarkdownConverter: null|HtmlToMarkdownConverter = null;
    private cssReadyPromise: Promise<void>;

    constructor() {
        super();
        this.widgetConfig = config;
        this.componentConfig = new ComponentConfig();
        this.widgetDOM = this.attachShadow({ mode: 'open' });
        this.cssReadyPromise = this.initCss();
        this.initErrorHandler();
    }

    // wird aufgerufen, wenn die Componente in den Dom gehängt wird
    async connectedCallback(): Promise<void> {
        await this.initContent();
    }

    set config(value: any) {
        this.componentConfig.setConfig(value);
    }

    initCss(): Promise<void>
    {
        return new Promise((resolve) => {
            const link = document.createElement('link');
            link.rel = "stylesheet";
            link.href = this.getCssUrl();

            const logger = new ClientErrorLogger();
            let resolved = false;
            const done = () => {
                if (resolved) return;
                resolved = true;
                resolve();
            };

            link.addEventListener('load', done, { once: true });
            link.addEventListener('error', () => {
                logger.logInfo(
                    `CSS konnte nicht geladen werden`,
                    'css_load_error',
                    { reason: `Stylesheet load error: ${link.href}` }
                );
                done();
            }, { once: true });

            setTimeout(() => {
                if (!resolved) {
                    logger.logInfo(
                        `CSS-Laden hat Timeout erreicht`,
                        'css_load_timeout',
                        { reason: `Timeout after 3s: ${link.href}` }
                    );
                }
                done();
            }, 5000);

            this.widgetDOM.append(link);
        });
    }

    initErrorHandler():void
    {
        let logger = new ClientErrorLogger();
        logger.initEventListener();
    }

    initChatWindowCheck(): void
    {
        let chatWindowCheckChannel = new BroadcastChannel('chatWindowChannel');
        chatWindowCheckChannel.postMessage(this.getCallId());

        chatWindowCheckChannel.onmessage = (messageEvent) => {
            if (messageEvent.data != this.getCallId()) {
                this.minimize();
            }
        }

        this.chatWindowChannel = chatWindowCheckChannel;
    }

    stopChatWindowCheck(): void
    {
        if (this.chatWindowChannel === null) {
            return;
        }

        this.chatWindowChannel.close();
    }

    async retryConnect(): Promise<void> {
        if (null === this.activeChat) {
            return;
        }

        if (!(this.activeChat instanceof AgentChat)) {
            return;
        }

        return this.activeChat.initConnection(this.userData, true)
    }

    private getCssUrl(): string
    {
        const widgetUrl = new URL(import.meta.url);
        const widgetDomain = widgetUrl.origin;

        //local dev
        if ( this.componentConfig.useLocalResources ) {
            return widgetDomain + "/ChatWidget/public/chat-widget-"+ this.widgetConfig.id + ".css";
        }
        else{
            return widgetDomain + "/chat-widget-"+ this.widgetConfig.id + ".css";
        }

    }

    async initContent(): Promise<void>
    {
        this.initCustomHtmlDirective();
        this.holdSession();
        this.initChatWindowCheck();

        if(this.isUsernameRequired()) {
            this.initUsernameContent();
        } else {
            this.initMessagesContent();
            await this.initChat();
        }

        this.initStartQuestion();
    }

    private initStartQuestion():void
    {
        if (!(this.activeChat instanceof BotChat)) {
            return;
        }
        if (this.componentConfig.startQuestion === null) {
            return;
        }

        let input = this.widgetDOM.getElementById('chat-widget-message-input');
        if(!(input instanceof HTMLTextAreaElement)) {
            return;
        }

        const alpineComponent = this.widgetDOM.querySelector('[x-data="messageContent"]');
        if (alpineComponent === null) {
            return;
        }

        input.value = this.componentConfig.startQuestion;
        alpineComponent?.dispatchEvent(new CustomEvent('send-current-message'));
    }

    stopHoldSession(): void
    {
        let intervalId = this.holdSessionIntervalId;
        if (intervalId === null) {
            return;
        }

        clearInterval(intervalId);
    }

    holdSession(): void
    {
        let renewSessionFunction = this.componentConfig.renewSession;

        if (renewSessionFunction === null || renewSessionFunction === undefined) {
            return;
        }

        this.holdSessionIntervalId = setInterval(() => {
            renewSessionFunction();
        }, 200000);
    }

    private getCallId(): string
    {
        if (this.callId !== null) {
            return this.callId;
        }

        let callId = Date.now().toString() ;
        if (typeof self.crypto?.randomUUID === 'function') {
            callId = self.crypto.randomUUID();
        }

        this.callId = callId;
        return callId;
    }

    private isUsernameRequired(): boolean
    {
        // only for local dev
        if ( this.componentConfig.showCloseContent || this.componentConfig.showMessageContent ) {
            let widget = this;
            if ( this.componentConfig.showCloseContent ){
                widget.initCloseContent();
            }
            if ( this.componentConfig.showMessageContent ){
                widget.initMessagesContent()
            }
        }

        return this.componentConfig.forceAgentChat &&
            this.componentConfig.userNameRequired &&
            this.componentConfig.agentChatId === null;

    }

    public initMessagesContent():void
    {
        const wrapper = this.createWidgetWrapperForContent(htmlMessagesContent, true);

        let widget = this;
        Alpine.data('chatWidgetContent', () => ({
            header: this.widgetConfig.getHeaderText(false),
            trademarkName: this.widgetConfig.getTrademarkName(),
            trademarkWebsite: this.widgetConfig.getTrademarkWebsite(),
            widgetConfig: this.widgetConfig,
            init(): void {
                this.$root.addEventListener('minimize-chat', () => {
                    this.minimizeChat();
                });
            },
            minimizeChat(): void {
                widget.minimize();
            },
            onSwitchToAgentChat(): void {
                this.header = this.widgetConfig.getHeaderText(true);
            },
            toggleFullscreen(): void {
                widget.toggleFullscreen();
            },
        }));

        Alpine.data(
            'messageContent',
            () => {
                return messageContentFactory(widget, this.widgetConfig, this.componentConfig);
            });

        Alpine.data(
            'speechMessage',
            () => {
                return speechMessageFactory(widget, this.widgetConfig, this.componentConfig);
            });

        Alpine.data(
            'feedbackComponent',
            (message: OutputMessageObject) => feedbackComponentFactory(this.widgetConfig, message),
        )

        Alpine.initTree(wrapper);
    }

    private initUsernameContent(): void
    {
        const wrapper = this.createWidgetWrapperForContent(htmlUsernameContent);

        const widget = this;
        Alpine.data('chatWidgetContent', () => ({
            header: this.widgetConfig.getHeaderText(true),

            minimizeChat(): void {
                widget.getAlpineComponent().closeWidget();
                widget.getChatHelper().initAgentAvailabilityCheck();
            },
            onSwitchToAgentChat(): void {
                // noop
            },
            toggleFullscreen(): void {
                widget.toggleFullscreen();
            }
        }));

        Alpine.data('usernameForm', () => usernameFormFactory(widget, this.widgetConfig));

        Alpine.initTree(wrapper);
    }

    private createWidgetWrapperForContent(htmlInnerContent: string, checkIfExists: boolean = false): HTMLDivElement
    {
        let chatWidgetWrapper = null;
        if(checkIfExists) {
            chatWidgetWrapper = this.widgetDOM.getElementById('chat-widget-wrapper');
        }

        if(chatWidgetWrapper instanceof HTMLDivElement) {
            const wrapper = chatWidgetWrapper;
            wrapper.innerHTML = htmlLayout.replace(this.innerContentReplaceDiv, htmlInnerContent);
            return wrapper;
        }

        const wrapper = document.createElement('div');
        wrapper.setAttribute('id', 'chat-widget-wrapper');
        wrapper.classList.add('chat-widget-wrapper');
        wrapper.innerHTML = htmlLayout.replace(this.innerContentReplaceDiv, htmlInnerContent);
        this.widgetDOM.appendChild(wrapper);
        return wrapper;
    }

    public setUserData(
        userFirstName: string,
        userLastName: string,
        subscriberId: number = 0,
        userSessionId: string = '',
        userChatSessionId: string = '',
        salutation: string = '',
        botChatSessionId: string = ''): void {
        this.userData = new UserData(userFirstName, userLastName, subscriberId, userSessionId,userChatSessionId, salutation, botChatSessionId);
    }

    async initChat(): Promise<void>
    {
        let chatMaker = new ChatMaker(this.widgetConfig, this.componentConfig, this.widgetDOM, this.getAlpineComponent());
        this.activeChat = await chatMaker.getChatToUse();

        this.addSystemLoader( SystemMessage.LOAD_CHAT );
        await this.cssReadyPromise; // auf css warten, bevor texte geladen werden

        if(this.activeChat instanceof BotChat) {
            this.reportingChat = this.activeChat;
            await this.getChatHelper().initAgentAvailabilityCheck();
        }

        if(this.activeChat instanceof AgentChat) {
            await this.activeChat.initConnection(this.userData);
        }

        this.addSystemLoader( SystemMessage.LOAD_HISTORY );
        await this.activeChat.loadHistory();
        await this.activeChat.waitForLoadedHistory();
        this.removeSystemLoader( SystemMessage.LOAD_HISTORY );

        this.removeSystemLoader( SystemMessage.LOAD_CHAT );
    }

    async switchToAgentChat(messages: OutputMessageObject[], botHistoryMessages: OutputMessageObject[]):Promise<void>
    {
        this.getChatHelper().setAgentAvailabilityCheckToEnd();
        this.getChatHelper().showAgentChatButton(false);

        // let summary = null;
        // if (this.activeChat instanceof BotChat) {
        //     let botChat = this.activeChat;
        //     summary = await botChat.getSummary();
        // }

        const maxAiHistoryTextLength = this.widgetConfig.getMaxAiHistoryTextLength();
        this.aiHistoryMessages = [];
        botHistoryMessages.forEach( (message: OutputMessageObject) => {
            this.addAiHistoryMessage(message, maxAiHistoryTextLength);
        });
        messages.forEach( (message: OutputMessageObject) => {
            this.addAiHistoryMessage(message, maxAiHistoryTextLength);
        });

        if(this.aiHistoryMessages.length !== 0) {
            let lastAiHistoryMessage = this.aiHistoryMessages[this.aiHistoryMessages.length - 1];
            lastAiHistoryMessage.isLastMessage = true;
        }

        await this.activeChat?.closeWebsocketConnection();
        this.activeChat = new AgentChat(this.widgetConfig, this.componentConfig, this.getAlpineComponent());
        await this.activeChat.initConnection();

        // if (summary !== null) {
        //     agentChat.sendSummary(summary);
        // }

        await this.initAiHistoryMessageSend();
    }

    public addAiHistoryMessage(message: OutputMessageObject, maxAiHistoryTextLength: number): void
    {
        if(message.type !== 'question' && message.type !== 'answer') {
            return;
        }

        const converter = this.getHtmlToMarkdownConverter();
        let aiHistoryForOutputMessage  = MessageFactory.createAiHistoryMessages(message, converter, maxAiHistoryTextLength);
        for (let i = 0; i < aiHistoryForOutputMessage.length; i++) {
            this.aiHistoryMessages.push(aiHistoryForOutputMessage[i]);
        }
    }

    public hasAiHistoryMessages(): boolean
    {
        return this.aiHistoryMessages.length > 0;
    }

    private getHtmlToMarkdownConverter(): HtmlToMarkdownConverter
    {
        if(this.htmlToMarkdownConverter instanceof HtmlToMarkdownConverter) {
            return this.htmlToMarkdownConverter;
        }

        this.htmlToMarkdownConverter = new HtmlToMarkdownConverter();
        return this.htmlToMarkdownConverter;
    }

    public setLastAiMessageFlag(): void {
        if (!this.hasAiHistoryMessages()) {
            return;
        }

        let lastAiHistoryMessage = this.aiHistoryMessages[this.aiHistoryMessages.length - 1];
        lastAiHistoryMessage.isLastMessage = true;
    }

    public async initAiHistoryMessageSend(): Promise<void> {
        if(!(this.activeChat instanceof AgentChat)) {
            return;
        }

        if(this.aiHistoryMessages.length === 0) {
            return;
        }

        if(this.aiHistoryMessages[this.aiHistoryMessageIndex] === undefined) {
            // Alle aiHistoryMessages wurden geschickt; Hilfsvariablen zurücksetzen
            this.aiHistoryMessages = [];
            this.aiHistoryMessageIndex = 0;
            return;
        }

        await this.activeChat.sendAiHistory(this.aiHistoryMessages[this.aiHistoryMessageIndex]);
        ++this.aiHistoryMessageIndex;
    }

    public getChatHelper(): ChatHelper
    {
        if(this.chatHelper instanceof ChatHelper) {
            return this.chatHelper;
        }

        let agentChatButton = this.widgetDOM.getElementById('agentChatButton');
        let chatHelper = new ChatHelper(this.widgetConfig, this.componentConfig, agentChatButton);
        this.chatHelper = chatHelper;
        return chatHelper;
    }

    public async sendChatMessage( addMessageToContent:(message:string, id: string, chatType: ChatType, type: string) => void, messageObject: BotMessageObject|AgentMessageObject ): Promise<void>
    {
        let input = this.widgetDOM.getElementById('chat-widget-message-input');
        if(!(input instanceof HTMLTextAreaElement)) {
            return;
        }

        let messageText = isBotMessageObject(messageObject) ? messageObject.messageText : messageObject.text;
        let chatType = this.activeChat instanceof BotChat ? ChatType.BOT_CHAT : ChatType.AGENT_CHAT;

        addMessageToContent(messageText, messageObject.messageId, chatType, 'question');
        input.value = '';
        const detail: IChatMessageInputHasTextEventDetails = {
            hasText: false,
            value: '',
        };
        window.dispatchEvent(new CustomEvent(CHAT_MESSAGE_INPUT_HAS_TEXT_EVENT, {
            detail,
        }));

        if(this.activeChat === null) {
            return;
        }

        this.activeChat.sendMessage(messageObject);
    }

    public getMessageObject(message: string, speechToText: boolean = false): BotMessageObject|AgentMessageObject {
        let chatType = this.activeChat instanceof BotChat ? ChatType.BOT_CHAT : ChatType.AGENT_CHAT;
        return MessageFactory.create(chatType, message, this.widgetConfig.trademark, 'Message', speechToText);
    }

    public setIsClientTyping(isTyping: boolean): void
    {
        this.isClientTyping = isTyping;
    }

    public setEndClientTyping(): void
    {
        this.endClientTyping = true;
    }

    public initClientTyping(): void
    {
        if(!(this.activeChat instanceof AgentChat)) {
            return;
        }

        this.endClientTyping = false;
        this.activeChat.sendClientTypingEvent();

        let intervalId = setInterval(() => {
            if(this.endClientTyping) {
                clearInterval(intervalId);
            } else {
                if(!(this.activeChat instanceof AgentChat)) {
                    return;
                }
                if(this.isClientTyping) {
                    this.activeChat.sendClientTypingEvent();
                }
            }
        }, 5000);
    }

    private minimize(): void
    {
        this.activeChat?.minimize();
        this.stopChatWindowCheck();
        this.stopHoldSession();
    }

    public async closeChatByUser(onError: boolean): Promise<void>
    {
        if(this.activeChat instanceof AgentChat && !onError) {
            await this.initCloseContent();
        } else {
            this.getChatHelper().setAgentAvailabilityCheckToEnd();
            this.activeChat?.closeWidget();
            this.activeChat?.closeConnection();
        }

        if(this.componentConfig.forceAgentChat) {
            this.getChatHelper().initAgentAvailabilityCheck();
        }

        this.stopChatWindowCheck();
        this.stopHoldSession();
    }

    private addSystemLoader( messageId:string ): void
    {
        const alpineComponent = this.widgetDOM.querySelector('[x-data="messageContent"]');
        if (alpineComponent === null) {
            return;
        }

        alpineComponent?.dispatchEvent(new CustomEvent('add-system-loader', {
            detail: {
                messageId: messageId,
            }
        }));
    }

    private removeSystemLoader( messageId:string ): void
    {
        const alpineComponent = this.widgetDOM.querySelector('[x-data="messageContent"]');
        if (alpineComponent === null) {
            return;
        }

        alpineComponent?.dispatchEvent(new CustomEvent('remove-system-loader', {
            detail: {
                messageId: messageId,
            }
        }));
    }

    private forceRemoveSystemLoader( ): void
    {
        const alpineComponent = this.widgetDOM.querySelector('[x-data="messageContent"]');
        if (alpineComponent === null) {
            return;
        }

        alpineComponent?.dispatchEvent(new CustomEvent('remove-all-system-loader', {}));
    }

    private initBotSuggestions(): void
    {
        const alpineComponent = this.widgetDOM.querySelector('[x-data="messageContent"]');
        if (alpineComponent === null) {
            return;
        }

        alpineComponent?.dispatchEvent(new CustomEvent('init-bot-suggestions', {}));
    }

    public async initCloseContent(closeContentType: ClosePageContentType = ClosePageContentType.PRINT): Promise<void>
    {
        const isAgentChat = this.activeChat instanceof AgentChat;
        if (closeContentType === ClosePageContentType.PRINT) {
            await this.activeChat?.waitForLoadedHistory();
        }
        this.forceRemoveSystemLoader();

        this.stopHoldSession();
        this.activeChat?.closeConnection();

        const cssUrl = this.getCssUrl();

        let printContent = null;
        let closeContent = this.widgetConfig.getCloseContent();
        if (closeContentType === ClosePageContentType.PRINT) {
            const messages = this.widgetDOM.getElementById('chat-widget-messages');
            if (messages instanceof HTMLElement) {
                printContent = messages.innerHTML;
            }
        }

        if (closeContentType === ClosePageContentType.NO_AGENT_AVAILABLE) {
            closeContent = this.widgetConfig.getNoAgentAvailableCloseContent();
        }

        const wrapper = this.createWidgetWrapperForContent(htmlCloseContent, true);
        const widget = this;

        Alpine.data('chatWidgetContent', () => ({
            init() {
                const minimizeButton = widget.widgetDOM.getElementById("minimize-button");
                if(minimizeButton instanceof HTMLButtonElement) {
                    minimizeButton.classList.add('hide');
                }
            },
            header: this.widgetConfig.getHeaderText(isAgentChat),
            trademarkName: this.widgetConfig.getTrademarkName(),
            trademarkWebsite: this.widgetConfig.getTrademarkWebsite(),
            closeContent1: closeContent.line1,
            closeContent2: closeContent.line2,
            closeContent3: closeContent.line3,
            printChatHistoryVisibility: closeContentType === ClosePageContentType.PRINT,
            closeWidget(): void {
                widget.activeChat?.closeWidget();
            },
            toggleFullscreen(): void {
                widget.toggleFullscreen();
            },
            printChatHistory(): void {
                if (closeContentType !== ClosePageContentType.PRINT) {
                    return;
                }

                if (!printContent || typeof printContent !== 'string') {
                    (new ClientErrorLogger()).logInfo('no printContent found for history');
                    return;
                }

                const rootNode = widget.widgetDOM?.getRootNode?.() as Document | ShadowRoot | undefined;
                const topDoc = (rootNode && (rootNode as ShadowRoot).host)
                    ? (rootNode as ShadowRoot).host.ownerDocument!
                    : document;

                let printPage = new PrintPage(topDoc, printContent, {cssUrls: [cssUrl]});
                printPage.show();
            },
            onSwitchToAgentChat(): void {
                // noop
            }
        }));

        Alpine.initTree(wrapper);
    }

    public getOutputMessageText(message: OutputMessageObject): string
    {
        let text = message.text;
        if(message.type !== 'answer') {
            return text;
        }

        let div = document.createElement("div");
        div.innerHTML = text;

        let linkElements = div.getElementsByTagName('a');
        if(linkElements.length <= 0) {
            return text;
        }

        for (let element of linkElements) {
            element.classList.add('reportingLink');
            element.setAttribute('data-message-id', message.messageId);
            element.setAttribute('data-response-id', message.responseId);
        }
        text = div.innerHTML;

        return text;
    }

    public triggerReportingEvent(messageId: string, responseId: string, targetLink: string) : void
    {
        const appDomain = window.location.hostname.toLowerCase();
        const domainValidator = new DomainValidator(this.widgetConfig, appDomain, targetLink);

        if (!domainValidator.isValid()) {
            let message = 'External link '+ targetLink +' is not allowed';
            (new ClientErrorLogger()).logInfo(message);
            return;
        }

        this.sendReportingEvent(messageId, responseId, domainValidator.getLink());
        window.open(domainValidator.getLink(), domainValidator.getLinkTarget());
    }

    private sendReportingEvent(messageId: string, responseId: string, link: string): void
    {
        if(this.reportingChat instanceof BotChat) {
            this.reportingChat.sendReportingEvent(messageId, responseId, link);
            return;
        }

        let reportingChat = new BotChat(this.widgetConfig, this.componentConfig, this.getAlpineComponent());
        reportingChat.initConnection().then(r =>
            reportingChat.sendReportingEvent(messageId, responseId, link),
        );
        this.reportingChat = reportingChat;
    }

    public getAlpineComponent(): AlpineComponent
    {
        if (this.alpineComponent === null) {
            this.alpineComponent = new AlpineComponent(this, this.widgetDOM, this.widgetConfig, this.componentConfig);
        }

        return this.alpineComponent;
    }

    public toggleFullscreen(): void
    {
        this.classList.toggle(this.fullscreenClassName);
    }

    private initCustomHtmlDirective() {
        Alpine.directive('raw', (el, { expression }, { effect, evaluateLater }) => {
            let evaluate = evaluateLater<string>(expression);

            effect(() => {
                evaluate((value) => {
                    Alpine.mutateDom(() => {
                        el.innerHTML = value ?? '';

                        el._x_ignoreSelf = true;
                        Alpine.initTree(el);
                        delete el._x_ignoreSelf;
                    })
                })
            })
        })
    }
}